import { useRef, useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, functions } from '../../firebase-client';
import { Link } from 'react-router-dom';
import EmailInput from './formComponents/EmailInput';
import UsernameInput from './formComponents/UsernameInput';
import PasswordInput from './formComponents/PasswordInput';
import DateInput from './formComponents/DateInput';
import SubmitButton from './formComponents/SubmitButton';
import ErrorMessage from './formComponents/ErrorMessage';
import { passwordMeetsRequirements } from '../../utils/passwordValidation';
import {
  LEGAL_UPDATE_SUMMARY,
  LEGAL_UPDATE_VERSION,
  PRIVACY_UPDATED_AT,
  TERMS_UPDATED_AT,
} from '../../utils/legalUpdates';

const syncMyAuthEmailFn = httpsCallable(functions, 'syncMyAuthEmail');
const requestEmailVerificationFn = httpsCallable(functions, 'requestEmailVerification');

export default function RegistrationForm({ onSuccess }) {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername]   = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [dob, setDob]             = useState('');
  const [consent, setConsent]     = useState(false);
  const [error, setError]         = useState(null);
  const [available, setAvailable] = useState(null);
  const [receiveEmail, setReceiveEmail] = useState(null);
  const [frequency, setFrequency] = useState('');
  const [progress, setProgress] = useState('');
  const submittingRef = useRef(false);
  const passwordValid = passwordMeetsRequirements(password);
  const passwordsMatch = password === confirmPassword;
  const canSubmit = Boolean(
    email.trim()
    && username.trim()
    && firstName.trim()
    && lastName.trim()
    && dob
    && passwordValid
    && passwordsMatch
    && available
    && consent
    && receiveEmail !== null
    && (!receiveEmail || frequency)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError(null);
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    if (!trimmedFirstName || !trimmedLastName || !dob) {
      setError('First name, last name, and date of birth are required.');
      return;
    }
    if (receiveEmail === null) {
      setError('Please choose whether to receive email notifications.');
      return;
    }
    if (receiveEmail && !frequency) {
      setError('Please select an email notification frequency.');
      return;
    }

    if (!passwordMeetsRequirements(password)) {
      setError('Password must be at least 10 characters and include uppercase, lowercase and a number.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (available === null) {
      setError('Checking username availability. Please try again in a moment.');
      return;
    }
    if (!available) {
      setError('Please choose a different username.');
      return;
    }
    if (!consent) {
      setError('You must agree to the terms.');
      return;
    }

    submittingRef.current = true;
    setProgress('Creating your account…');
    try {
      const savedFrequency = receiveEmail ? frequency : 'never';
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;

      setProgress('Saving your profile…');
      await Promise.all([
        setDoc(doc(db, 'users', uid), {
          username: username.toLowerCase(),
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          avatarUrl: null,
          joinDate: new Date().toISOString(),
          legalAcknowledgements: {
            [LEGAL_UPDATE_VERSION]: {
              acknowledgedAt: serverTimestamp(),
              termsUpdatedAt: TERMS_UPDATED_AT,
              privacyUpdatedAt: PRIVACY_UPDATED_AT,
              summary: LEGAL_UPDATE_SUMMARY,
              source: 'registration',
            },
          },
        }),
        setDoc(doc(db, 'privateUsers', uid), {
          dob,
          profileLastModified: serverTimestamp(),
          createdAt: new Date().toISOString(),
          communicationPreferences: {
            receiveEmail,
            frequency: savedFrequency
          }
        })
      ]);
      await syncMyAuthEmailFn();

      setProgress('Sending your verification email…');
      let verificationEmailSent = false;
      try {
        await requestEmailVerificationFn();
        verificationEmailSent = true;
      } catch {
        // Account creation succeeded. Let the verification screen offer a retry
        // instead of encouraging the member to submit registration again.
      }

      onSuccess?.({ verificationEmailSent });
    } catch (err) {
      setError(err.message);
    } finally {
      submittingRef.current = false;
      setProgress('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex-column" style={{ gap: '1rem' }} autoComplete="on">
      <p className="text-muted" style={{ margin: 0 }}>
        Create an account for yourself using your own name and date of birth.
      </p>
      <EmailInput
        inputId="registration-email"
        value={email}
        onChange={e => setEmail(e.target.value)}
      />
      <PasswordInput
        inputId="registration-password"
        name="newPassword"
        value={password}
        onChange={e => setPassword(e.target.value)}
        confirmInputId="registration-confirm-password"
        confirmName="confirmPassword"
        confirmValue={confirmPassword}
        onConfirmChange={e => setConfirmPassword(e.target.value)}
        showRequirements
      />
      <div className="flex-column" style={{ gap: '0.35rem' }}>
        <label htmlFor="registration-username" style={{ fontWeight: 600 }}>Username (public)</label>
        <p id="registration-username-help" className="text-muted" style={{ margin: 0, fontSize: '0.875rem' }}>
          Choose a short name that other members may see. This is not used to log in—you’ll use your email address.
        </p>
        <UsernameInput
          inputId="registration-username"
          value={username}
          onChange={setUsername}
          setAvailable={setAvailable}
          describedBy="registration-username-help"
        />
      </div>
      <div className="flex-column" style={{ gap: '0.35rem' }}>
        <label htmlFor="registration-first-name" style={{ fontWeight: 600 }}>Your first name</label>
        <p id="registration-name-help" className="text-muted" style={{ margin: 0, fontSize: '0.875rem' }}>
          Enter your own name as your club knows you. Clubs use it to identify you on memberships, bookings and events.
        </p>
        <input
          id="registration-first-name"
          name="given-name"
          autoComplete="given-name"
          type="text"
          placeholder="e.g. Alex"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          className="form-input"
          aria-describedby="registration-name-help"
          required
        />
      </div>
      <div className="flex-column" style={{ gap: '0.35rem' }}>
        <label htmlFor="registration-last-name" style={{ fontWeight: 600 }}>Your last name (surname)</label>
        <input
          id="registration-last-name"
          name="family-name"
          autoComplete="family-name"
          type="text"
          placeholder="e.g. Smith"
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          className="form-input"
          aria-describedby="registration-name-help"
          required
        />
      </div>
      <DateInput
        label="Your date of birth"
        inputId="registration-dob"
        value={dob}
        onChange={e => setDob(e.target.value)}
        required
      />
      <fieldset style={{ border: '1px solid #ddd', padding: '1rem' }}>
        <legend>Email Notifications (required)</legend>

        <p style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Receive notifications via email?
        </p>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
          <label>
            <input
              type="radio"
              name="receive-email"
              checked={receiveEmail === true}
              onChange={() => {
                setReceiveEmail(true);
                setFrequency('');
              }}
              required
            />{' '}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="receive-email"
              checked={receiveEmail === false}
              onChange={() => {
                setReceiveEmail(false);
                setFrequency('never');
              }}
              required
            />{' '}
            No
          </label>
        </div>

        <div style={{ marginLeft: '1rem', opacity: receiveEmail === true ? 1 : 0.5 }}>
          {['instant', 'daily', 'weekly'].map(option => (
            <label key={option} style={{ display: 'block', margin: '0.25rem 0' }}>
              <input
                type="radio"
                name="frequency"
                value={option}
                checked={frequency === option}
                onChange={() => setFrequency(option)}
                disabled={receiveEmail !== true}
                required={receiveEmail === true}
              />
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </label>
          ))}
        </div>

        <p style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.5rem' }}>
          You can update your preferences later in your profile. Critical messages will always be sent.
        </p>
      </fieldset>

      <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.9rem' }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={e => setConsent(e.target.checked)}
          required
          style={{ marginTop: '0.2rem' }}
        />
        <span>
          I agree to the{' '}
          <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="link">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link">
            Privacy Policy
          </Link>.
        </span>
      </label>

      <SubmitButton label={progress || 'Register'} disabled={!canSubmit || Boolean(progress)} />

      <p role="status" aria-live="polite" aria-atomic="true" className="text-muted" style={{ margin: 0 }}>
        {progress && `${progress} Please keep this page open.`}
      </p>

      <ErrorMessage message={error} />
    </form>
  );
}
